import QRCode from "@/assets/icons/QRCode";
import Swap from "@/assets/icons/Swap";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import { Center } from "@/components/ui/center";
import { Fab, FabLabel } from "@/components/ui/fab";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { T, useT } from "@transifex/react";
import { type BarcodeScanningResult, type CameraMountError, type CameraType, CameraView, useCameraPermissions } from "expo-camera";
import { router } from "expo-router";
import { useState } from "react";
import { Platform } from "react-native";
import app from "../../../app.json";

export default function Qr() {
  const appName = app.expo.name;
  const [facing, setFacing] = useState<CameraType>("back");
  const [message, setMessage] = useState<string>("");
  const [permission, requestPermission] = useCameraPermissions();
  const t = useT();

  const handleQRCode = (qr: BarcodeScanningResult): void => {
    setMessage("QR code found!");
    router.replace(`/welcome?qr=${qr.data}`);
  };

  const handleCameraError = (error: CameraMountError) => {
    console.error(error);
    setMessage(error.message);
  };

  const handleCameraSwap = (): void => {
    setFacing(facing === "back" ? "front" : "back");
  };

  function renderCamera() {
    return (
      <CameraView
        autofocus="on"
        barcodeScannerSettings={{
          barcodeTypes: ["qr"],
        }}
        facing={facing}
        onBarcodeScanned={handleQRCode}
        onMountError={handleCameraError}
        style={{ flex: 1, overflow: "hidden" }}
        ratio="16:9"
      >
        <Center className="flex-1">
          <QRCode fill="white" height={240} style={{ opacity: 0.2 }} width={240} />
        </Center>
        {Platform.OS !== "web" && (
          <Fab onPress={handleCameraSwap} placement="bottom center" size="sm">
            <Swap />
            <FabLabel>
              <Text className="text-typography-900">
                <T _str="Swap Camera" />
              </Text>
            </FabLabel>
          </Fab>
        )}
      </CameraView>
    );
  }

  function renderMessage() {
    return (
      <VStack className="flex-1 gap-[32px]">
        <Text className="text-typography-900 self-center">{message}</Text>
        <AppButton onPress={() => setMessage("")} title={t("Retry")} />
      </VStack>
    );
  }

  function renderPermission() {
    return <AppButton onPress={requestPermission} title={t("Allow {appName} to access camera", { appName: appName })} />;
  }

  if (!permission) {
    // Camera permissions are missing
    return <QrScreenSkeleton />;
  }

  return (
    <Screen className="flex-1">
      <VStack className="flex-1 gap-[32px]">
        <PageTitle title={t("Scan QR Parking Code")} />
        {!permission.granted && renderPermission()}
        {permission.granted && !message && renderCamera()}
        {permission.granted && !!message && renderMessage()}
      </VStack>
    </Screen>
  );
}

function QrScreenSkeleton() {
  return (
    <Screen>
      <VStack className="flex-1 justify-center gap-[32px]">
        <SkeletonText _lines={1} className="h-6 w-3/4 self-center" />
        <Center className="flex-1">
          <Skeleton className="w-full h-[52px]" />
        </Center>
      </VStack>
    </Screen>
  );
}
